/**
 * Status, filter, and display config for activities.
 */
import { CheckCircle2, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';

/** Badge + icon config for an activity status. */
export interface ActivityStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Badge config for each activity status. */
export const ACTIVITY_STATUS_CONFIG: Record<string, ActivityStatusConfig> = {
  active: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
  },
  inactive: {
    label: 'Inactif',
    icon: XCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** Activity list filter state. */
export interface ActivityFilters {
  statuses: string[];
  activityTypeIds: string[];
  searchQuery?: string;
}

/** Default activity list filters. */
export const DEFAULT_ACTIVITY_FILTERS: ActivityFilters = {
  statuses: [],
  activityTypeIds: [],
  searchQuery: '',
};

/** Filter group IDs for the activities {@link DataTable} configure panel. */
export type ActivityFilterGroupId = 'filter-status' | 'filter-type-activity' | 'filter-search';

/** Configure-panel order for activity filter groups. */
export const ALL_ACTIVITY_FILTER_GROUP_IDS: ActivityFilterGroupId[] = [
  'filter-status',
  'filter-type-activity',
  'filter-search',
];

/** Human-readable label per filter group (configure panel). */
export const FILTER_GROUP_LABELS_ACTIVITIES: Record<ActivityFilterGroupId, string> = {
  'filter-status': 'Statut',
  'filter-type-activity': "Type d'activité",
  'filter-search': 'Recherche',
};

/** Returns the badge color name for an activity status. */
export function getActivityStatusColor(status: string): 'success' | 'error' {
  return status === 'active' ? 'success' : 'error';
}

/** Empty create form — status defaults to `active`. */
export const DEFAULT_CREATE_ACTIVITY_FORM = {
  activityTypeId: '',
  code: '',
  label: '',
  description: '',
  status: 'active' as const,
};

/** Shared React Query key for the activity-type catalog. */
export const ACTIVITY_TYPES_QUERY_KEY = ['activityTypes'] as const;
